import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Search, ChevronRight, AlertTriangle, AlertCircle, Clock, CheckCircle,
  Activity, Eye, FileText, X, Zap, Target, TrendingUp, RotateCcw, Filter,
  Lightbulb, BookOpen, Clipboard, User, Calendar, Stethoscope, HelpCircle,
  ChevronDown, Copy, Check, Camera, Upload, Trash2, Sparkles, Loader2, FileDown
} from 'lucide-react';
import Peer from 'peerjs';
import QRCode from 'qrcode';
import {
  symptomes, signes, radios, pathologies, rules,
  urgenceConfig, catColors, symptomeCategories, signeCategories, localisation
} from '../data/diagnosticData';
import DentalChart from './DentalChart';
import DiagnosticPDF from './DiagnosticPDF';

// Recommandations therapeutiques par pathologie
const treatmentRecommendations = {
  "PAT001": ["Reminéralisation topique (fluor)", "Conseils d'hygiène", "Suivi 3-6 mois"],
  "PAT002": ["Restauration amélo-dentinaire", "Scellement des sillons si indiqué"],
  "PAT003": ["Restauration composite/amalgame", "Coiffage indirect si proche pulpe"],
  "PAT004": ["Coiffage indirect ou direct", "Surveillance vitalité pulpaire", "Si symptômes : traitement endodontique"],
  "PAT005": ["Élimination de l'étiologie", "Coiffage indirect", "Surveillance rapprochée"],
  "PAT006": ["Traitement endodontique en urgence", "Analgésie", "Antibioprophylaxie si indiquée"],
  "PAT008": ["Traitement endodontique", "Restauration coronaire"],
  "PAT009": ["Traitement endodontique", "Drainage si nécessaire", "Antalgiques"],
  "PAT010": ["Drainage chirurgical ou endodontique", "Antibiothérapie", "Antalgiques", "Surveillance"],
  "PAT011": ["Traitement endodontique", "Suivi radiologique"],
  "PAT012": ["Traitement endodontique", "Résection apicale si échec"],
  "PAT013": ["Hospitalisation si signes de gravité", "Antibiothérapie IV", "Drainage chirurgical"],
  "PAT014": ["URGENCE HOSPITALIÈRE", "Antibiothérapie IV", "Réanimation si nécessaire"],
  "PAT015": ["Traitement endodontique + chirurgie apicale", "Énucléation kystique"],
  "PAT016": ["Traitement endodontique précoce", "Hydroxyde de calcium intra-canalaire"],
  "PAT020": ["Détartrage-polissage", "Motivation à l'hygiène", "Contrôle 3 mois"],
  "PAT022": ["Débridement sous anesthésie", "Métronidazole", "Bains de bouche CHX", "Antalgiques"],
  "PAT023": ["Idem GUN + surfaçage radiculaire", "Suivi parodontal rapproché"],
  "PAT024": ["Thérapeutique parodontale non chirurgicale", "Surfaçage", "Réévaluation"],
  "PAT025": ["Surfaçage radiculaire", "Réévaluation à 3 mois", "Chirurgie si poches persistantes"],
  "PAT026": ["Traitement parodontal complet", "Chirurgie parodontale", "Contention si mobilité"],
  "PAT027": ["Plan de traitement global", "Extractions si pronostic défavorable", "Réhabilitation"],
  "PAT031": ["Drainage", "Antiseptiques locaux", "Élimination facteur étiologique"],
  "PAT032": ["Drainage + curetage de la poche", "Antibiothérapie si diffusion", "Surfaçage"],
  "PAT033": ["Traitement endodontique prioritaire", "Puis traitement parodontal", "Réévaluation"],
  "PAT041": ["Réduction des contacts occlusaux", "Restauration avec cerclage", "Surveillance"],
  "PAT042": ["Restauration composite", "Surveillance vitalité"],
  "PAT043": ["Pulpotomie ou pulpectomie selon maturité", "Restauration hermétique"],
  "PAT045": ["Contention rigide 3-4 mois", "Traitement endo si nécrose", "Suivi radiologique"],
  "PAT051": ["Vernis fluoré/désensibilisant", "Dentifrice spécifique", "Traitement étiologique"],
  "PAT052": ["Identification et élimination des facteurs acides", "Restauration si perte importante"],
  "PAT054": ["Gouttière de protection nocturne", "Restauration si perte fonctionnelle"],
  // Phase 1 - Nouvelles pathologies
  "PAT055": ["Dépose de la restauration", "Curetage carieux", "Nouvelle restauration étanche", "Contrôle à 6 mois"],
  "PAT056": ["CBCT pour évaluation 3D", "Surveillance si débutante", "Traitement endo + chirurgie si étendue", "Extraction si pronostic défavorable"]
};

// Référentiel de traduction des codes sémiologiques pour l'analyse IA
const criteriaTranslations = {
  // Localisations
  "JUG_G": "Joue gauche",
  "JUG_D": "Joue droite",
  "LANG_DOS": "Dos de langue",
  "LANG_BORD_G": "Bord latéral langue gauche",
  "LANG_BORD_D": "Bord latéral langue droit",
  "LANG_VENTRE": "Face ventrale langue",
  "LANG_POINTE": "Pointe de langue",
  "PALAIS_DUR": "Palais dur",
  "PALAIS_MOU": "Palais mou",
  "PLANCHER": "Plancher buccal",
  "GENCIVE_SUP": "Gencive supérieure",
  "GENCIVE_INF": "Gencive inférieure",
  "LEVRE_SUP": "Lèvre supérieure",
  "LEVRE_INF": "Lèvre inférieure",
  "COMMISSURE": "Commissure labiale",
  "VESTIBULE": "Vestibule",
  "AMYGDALE": "Amygdale",
  "TRIGONE": "Trigone rétromolaire",

  // Couleurs
  "C01": "Blanc", "C02": "Rouge", "C03": "Rose", "C04": "Brun/Marron",
  "C05": "Noir", "C06": "Jaune", "C07": "Gris", "C08": "Bleu/Violacé",
  "C09": "Couleur muqueuse normale", "C10": "Bicolore/Hétérogène",

  // Surfaces/Textures
  "S01": "Lisse", "S02": "Rugueuse", "S03": "Verruqueuse", "S04": "Papillomateuse",
  "S05": "Granuleuse", "S06": "Kératosique", "S07": "Ulcérée", "S08": "Érosive",
  "S09": "Croûteuse", "S10": "Fissurée", "S11": "Ponctuée", "S12": "Striée",
  "S13": "Réticulée", "S14": "Homogène", "S15": "Hétérogène",

  // Reliefs
  "R01": "Plat/Plan", "R02": "Surélevé", "R03": "Déprimé/Ulcéré", "R04": "Nodulaire",
  "R05": "Papuleux", "R06": "Vésiculeux", "R07": "Bulleux", "R08": "Polypoïde",
  "R09": "Sessile", "R10": "Pédiculé", "R11": "Maculo-papuleux", "R12": "Exophytique",
  "R13": "Endophytique", "R14": "Végétant", "R15": "Bourgeonnant",
  "R21": "Légèrement surélevé", "R22": "Plat", "R23": "Déprimé",

  // Nombre
  "N1": "Unique", "N2": "Multiple (2-5)", "N3": "Nombreuses (>5)", "N4": "Confluentes",

  // Distribution
  "diffuse": "Diffuse", "localisee": "Localisée", "bilaterale": "Bilatérale",
  "unilaterale": "Unilatérale", "symetrique": "Symétrique", "asymetrique": "Asymétrique",

  // Bords
  "B01": "Nets/Réguliers", "B02": "Flous/Irréguliers", "B03": "Surélevés",
  "B04": "Éversés", "B05": "Festonnés", "B06": "Bien délimités", "B07": "Mal délimités",
  "B11": "Réguliers", "B12": "Irréguliers", "B21": "Nets", "B22": "Flous",
  "B31": "Bien définis", "B32": "Mal définis",

  // Taille
  "T01": "< 5mm", "T02": "5-10mm", "T03": "10-20mm", "T04": "> 20mm",

  // Consistance
  "K01": "Molle", "K02": "Ferme", "K03": "Dure/Indurée", "K04": "Fluctuante",

  // Halo
  "H01": "Absent", "H02": "Érythémateux", "H03": "Inflammatoire",

  // Labels français courants (si déjà traduits par le serveur)
  "present": "Présent", "absent": "Absent", "oui": "Oui", "non": "Non"
};

// Fonction pour traduire un critère - gère les codes inconnus intelligemment
function translateCriterion(criterion) {
  if (!criterion || typeof criterion !== 'string') return String(criterion || '');

  // Si c'est déjà une phrase lisible (contient des espaces et pas de code), retourner tel quel
  if (criterion.includes(' ') && !criterion.match(/:\s*[A-Z]\d+$/)) {
    return criterion;
  }

  // Vérifier si c'est un format "Label: CODE"
  const colonMatch = criterion.match(/^(.+?):\s*(.+)$/);
  if (colonMatch) {
    const [, label, code] = colonMatch;
    const translatedCode = criteriaTranslations[code.trim()];

    // Si le code est traduit, afficher "Label: Traduction"
    if (translatedCode) {
      return `${label}: ${translatedCode}`;
    }

    // Si le code ressemble à un code (lettre + chiffres), ne pas l'afficher
    if (/^[A-Z]\d+$/.test(code.trim())) {
      return label; // Afficher seulement le label sans le code
    }

    // Sinon retourner tel quel (le code est peut-être déjà lisible)
    return criterion;
  }

  // Essayer de traduire directement
  const directTranslation = criteriaTranslations[criterion];
  if (directTranslation) {
    return directTranslation;
  }

  // Si c'est un code seul (lettre + chiffres), ne pas l'afficher
  if (/^[A-Z]\d+$/.test(criterion)) {
    return ''; // Retourner vide pour les codes non traduits seuls
  }

  return criterion;
}

// Logo SVG Component - style HelloParo
function LogoSVG() {
  return (
    <svg viewBox="0 0 120 40" className="h-10">
      <defs>
        <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0EA5E9" />
          <stop offset="100%" stopColor="#8B5CF6" />
        </linearGradient>
      </defs>
      {/* Three stylized teeth */}
      <g fill="url(#logoGradient)">
        <path d="M8 8c0-2 1-4 4-4s4 2 4 4v16c0 4-2 8-4 12-2-4-4-8-4-12V8z" />
        <path d="M20 6c0-2 1-4 4-4s4 2 4 4v18c0 4-2 8-4 12-2-4-4-8-4-12V6z" />
        <path d="M32 8c0-2 1-4 4-4s4 2 4 4v16c0 4-2 8-4 12-2-4-4-8-4-12V8z" />
      </g>
      {/* Text */}
      <text x="48" y="18" className="text-sm" fill="#64748b" fontWeight="400" fontFamily="Inter, sans-serif">Hello</text>
      <text x="48" y="32" className="text-sm" fill="#0EA5E9" fontWeight="700" fontFamily="Inter, sans-serif" letterSpacing="0.05em">DIAGNOSTIC</text>
    </svg>
  );
}

// Disclaimer Modal Component
function DisclaimerModal({ onAccept }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4 rounded-t-2xl">
          <div className="flex items-center gap-3 text-white">
            <AlertTriangle className="w-8 h-8" />
            <h2 className="text-xl font-bold">Avertissement important</h2>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-4 text-slate-700">
          <p className="text-sm leading-relaxed">
            <strong>HelloDiagnostic</strong> est un outil d'aide au diagnostic destiné exclusivement aux{' '}
            <strong>professionnels de santé</strong> (chirurgiens-dentistes, médecins, stomatologues).
          </p>

          <p className="text-sm leading-relaxed">
            Cet outil utilise l'intelligence artificielle pour proposer des hypothèses diagnostiques
            à partir de l'analyse d'images cliniques. Les résultats fournis sont des{' '}
            <strong>suggestions à visée informative</strong> et ne constituent en aucun cas un diagnostic médical définitif.
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm font-semibold text-amber-800 mb-2">
              HelloDiagnostic ne saurait se substituer :
            </p>
            <ul className="text-sm text-amber-700 space-y-1.5">
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">•</span>
                À l'examen clinique complet du patient
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">•</span>
                Au jugement et à l'expertise du praticien
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-500 mt-0.5">•</span>
                Aux examens complémentaires nécessaires (biopsie, analyses, imagerie)
              </li>
            </ul>
          </div>

          <p className="text-sm leading-relaxed">
            Le praticien reste <strong>seul responsable</strong> du diagnostic final et des décisions thérapeutiques.
          </p>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-sm text-red-700">
              <strong>En cas de doute ou de suspicion de lésion maligne</strong>, une consultation spécialisée
              et/ou une biopsie doivent être réalisées <strong>sans délai</strong>.
            </p>
          </div>

          <p className="text-sm leading-relaxed text-slate-600 italic">
            En utilisant cet outil, vous confirmez être un professionnel de santé et acceptez ces conditions.
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-slate-50 rounded-b-2xl border-t border-slate-200">
          <button
            onClick={onAccept}
            className="w-full py-3 bg-gradient-to-r from-sky-500 to-indigo-500 text-white rounded-xl font-semibold hover:from-sky-600 hover:to-indigo-600 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
          >
            <CheckCircle className="w-5 h-5" />
            J'ai compris et j'accepte
          </button>
        </div>
      </div>
    </div>
  );
}

// Hook personnalisé pour le moteur diagnostique
function useDiagnosticEngine(selectedSymptomes, selectedSignes, selectedRadios, itemLocalisations = {}) {
  return useMemo(() => {
    if (selectedSymptomes.length === 0 && selectedSignes.length === 0 && selectedRadios.length === 0) {
      return [];
    }

    const results = [];

    for (const rule of rules) {
      const symMatch = rule.syms.filter(s => selectedSymptomes.includes(s)).length;
      const signMatch = rule.signs.filter(s => selectedSignes.includes(s)).length;
      const radioMatch = rule.radios ? rule.radios.filter(r => selectedRadios.includes(r)).length : 0;

      const symTotal = rule.syms.length;
      const signTotal = rule.signs.length;
      const radioTotal = rule.radios ? rule.radios.length : 0;

      const totalRequired = symTotal + signTotal + radioTotal;
      const totalMatched = symMatch + signMatch + radioMatch;

      if (totalMatched === 0) continue;

      const matchRatio = totalMatched / totalRequired;
      const adjustedScore = Math.round(rule.score * matchRatio);
      const fullMatch = totalMatched === totalRequired;
      const finalScore = fullMatch ? adjustedScore : Math.round(adjustedScore * 0.85);

      if (finalScore >= 40) {
        // Calculer les dents concernées pour ce diagnostic
        const matchedSyms = rule.syms.filter(s => selectedSymptomes.includes(s));
        const matchedSigns = rule.signs.filter(s => selectedSignes.includes(s));
        const matchedRadios = rule.radios ? rule.radios.filter(r => selectedRadios.includes(r)) : [];

        // Agréger les localisations de tous les éléments matchés
        const affectedTeeth = new Set();
        [...matchedSyms, ...matchedSigns, ...matchedRadios].forEach(itemId => {
          const locs = itemLocalisations[itemId] || [];
          locs.forEach(tooth => affectedTeeth.add(tooth));
        });

        const existing = results.find(r => r.id === rule.result);
        if (existing) {
          if (finalScore > existing.score) {
            existing.score = finalScore;
            existing.fullMatch = fullMatch;
            existing.matchedSyms = matchedSyms;
            existing.matchedSigns = matchedSigns;
            existing.matchedRadios = matchedRadios;
            existing.missingSyms = rule.syms.filter(s => !selectedSymptomes.includes(s));
            existing.missingSigns = rule.signs.filter(s => !selectedSignes.includes(s));
            existing.missingRadios = rule.radios ? rule.radios.filter(r => !selectedRadios.includes(r)) : [];
            existing.affectedTeeth = [...affectedTeeth];
          } else {
            // Fusionner les dents affectées même si le score est inférieur
            affectedTeeth.forEach(t => existing.affectedTeeth.push(t));
            existing.affectedTeeth = [...new Set(existing.affectedTeeth)];
          }
        } else {
          results.push({
            id: rule.result,
            ...pathologies[rule.result],
            score: finalScore,
            fullMatch,
            matchedSyms,
            matchedSigns,
            matchedRadios,
            missingSyms: rule.syms.filter(s => !selectedSymptomes.includes(s)),
            missingSigns: rule.signs.filter(s => !selectedSignes.includes(s)),
            missingRadios: rule.radios ? rule.radios.filter(r => !selectedRadios.includes(r)) : [],
            treatments: treatmentRecommendations[rule.result] || [],
            affectedTeeth: [...affectedTeeth]
          });
        }
      }
    }

    return results.sort((a, b) => b.score - a.score).slice(0, 8);
  }, [selectedSymptomes, selectedSignes, selectedRadios, itemLocalisations]);
}

// Composant principal
export default function DiagnosticTree() {
  // Disclaimer acceptance state - checks sessionStorage for current session
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage.getItem('hellodiagnostic_disclaimer_accepted') === 'true';
    }
    return false;
  });

  const handleAcceptDisclaimer = useCallback(() => {
    sessionStorage.setItem('hellodiagnostic_disclaimer_accepted', 'true');
    setDisclaimerAccepted(true);
  }, []);

  const [selectedSymptomes, setSelectedSymptomes] = useState([]);
  const [selectedSignes, setSelectedSignes] = useState([]);
  const [selectedRadios, setSelectedRadios] = useState([]);
  // Dents actuellement sélectionnées dans le schéma dentaire
  const [activeTeeth, setActiveTeeth] = useState([]);
  // Localisations associées à chaque élément sélectionné: { itemId: ["11", "12", ...] }
  const [itemLocalisations, setItemLocalisations] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("symptomes");
  const [expandedDiag, setExpandedDiag] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [showReferentielModal, setShowReferentielModal] = useState(false);
  const [referentielTab, setReferentielTab] = useState('overview');
  const [patientInfo, setPatientInfo] = useState({
    nom: '',
    prenom: '',
    date: new Date().toISOString().split('T')[0],
    praticien: '',
    centre: '',
    patientId: '',
    praticienId: '',
    centreId: ''
  });

  // Photos state
  const [photos, setPhotos] = useState([]);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [peerStatus, setPeerStatus] = useState('idle'); // idle, waiting, connected
  const peerRef = useRef(null);
  const connRef = useRef(null);

  // Photo analysis state - tracks analysis status and results per photo
  const [photoAnalysis, setPhotoAnalysis] = useState({}); // { [photoId]: { status: 'idle'|'analyzing'|'done'|'error', result: {...} } }

  // Function to analyze a photo via webhook
  const analyzePhoto = useCallback(async (photoId, imageData) => {
    // Set status to analyzing
    setPhotoAnalysis(prev => ({
      ...prev,
      [photoId]: { status: 'analyzing', result: null }
    }));

    try {
      // Extract base64 data (remove data:image/...;base64, prefix if present)
      const base64Data = imageData.includes(',') ? imageData.split(',')[1] : imageData;

      // POST to webhook
      const response = await fetch('https://n8n.cemedis.app/webhook/ffda5a78-99e2-46a5-bb5d-b2395d6335cd', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ img_b64: base64Data })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      // Store webhook result directly - server already does the matching via Gemini Vision API
      setPhotoAnalysis(prev => ({
        ...prev,
        [photoId]: { status: 'done', result }
      }));
    } catch (error) {
      console.error('Error analyzing photo:', error);
      setPhotoAnalysis(prev => ({
        ...prev,
        [photoId]: { status: 'error', result: null, error: error.message }
      }));
    }
  }, []);

  // Parse URL parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlPatientInfo = {
      nom: params.get('Patient_nom') || '',
      prenom: params.get('Patient_prenom') || '',
      date: new Date().toISOString().split('T')[0],
      praticien: params.get('Praticien_nom') || '',
      centre: params.get('Centre_nom')?.replace(/\u00A0/g, ' ').trim() || '',
      patientId: params.get('Patient_id') || '',
      praticienId: params.get('ID_praticien') || '',
      centreId: params.get('ID_centre') || ''
    };

    // Only update if we have URL params
    if (urlPatientInfo.nom || urlPatientInfo.prenom || urlPatientInfo.praticien) {
      setPatientInfo(urlPatientInfo);
    }
  }, []);

  const toggleItem = useCallback((id, list, setList) => {
    if (list.includes(id)) {
      setList(list.filter(i => i !== id));
      // Supprimer la localisation si l'item est désélectionné
      setItemLocalisations(prev => {
        const newLoc = { ...prev };
        delete newLoc[id];
        return newLoc;
      });
    } else {
      setList([...list, id]);
      // Associer les dents actuellement sélectionnées à cet élément
      if (activeTeeth.length > 0) {
        setItemLocalisations(prev => ({
          ...prev,
          [id]: [...activeTeeth]
        }));
      }
    }
  }, [activeTeeth]);

  const resetAll = useCallback(() => {
    setSelectedSymptomes([]);
    setSelectedSignes([]);
    setSelectedRadios([]);
    setActiveTeeth([]);
    setItemLocalisations({});
    setExpandedDiag(null);
    setSearchTerm("");
    setCategoryFilter(null);
  }, []);

  const resetSymptomes = useCallback(() => {
    setSelectedSymptomes(prev => {
      prev.forEach(id => {
        setItemLocalisations(locs => {
          const newLocs = { ...locs };
          delete newLocs[id];
          return newLocs;
        });
      });
      return [];
    });
  }, []);

  const resetSignes = useCallback(() => {
    setSelectedSignes(prev => {
      prev.forEach(id => {
        setItemLocalisations(locs => {
          const newLocs = { ...locs };
          delete newLocs[id];
          return newLocs;
        });
      });
      return [];
    });
  }, []);

  const resetRadios = useCallback(() => {
    setSelectedRadios(prev => {
      prev.forEach(id => {
        setItemLocalisations(locs => {
          const newLocs = { ...locs };
          delete newLocs[id];
          return newLocs;
        });
      });
      return [];
    });
  }, []);

  const handleAddCriteria = useCallback((type, id) => {
    switch (type) {
      case 'symptome':
        toggleItem(id, selectedSymptomes, setSelectedSymptomes);
        break;
      case 'signe':
        toggleItem(id, selectedSignes, setSelectedSignes);
        break;
      case 'radio':
        toggleItem(id, selectedRadios, setSelectedRadios);
        break;
    }
  }, [selectedSymptomes, selectedSignes, selectedRadios, toggleItem]);

  const diagnosticResults = useDiagnosticEngine(selectedSymptomes, selectedSignes, selectedRadios, itemLocalisations);

  // Differential diagnosis when scores are close (within 8 points)
  const differentialInfo = useMemo(() => {
    if (diagnosticResults.length < 2) return null;

    const THRESHOLD = 8; // points difference threshold
    const top1 = diagnosticResults[0];
    const closeResults = diagnosticResults.slice(1).filter(d => top1.score - d.score <= THRESHOLD);

    if (closeResults.length === 0) return null;

    // Find discriminating tests for each pair
    const discriminatingTests = [];
    const allClose = [top1, ...closeResults];

    // Get unique missing criteria across all close diagnoses
    const allMissingSyms = new Set();
    const allMissingSigns = new Set();
    const allMissingRadios = new Set();

    allClose.forEach(diag => {
      diag.missingSyms?.forEach(s => allMissingSyms.add(s));
      diag.missingSigns?.forEach(s => allMissingSigns.add(s));
      diag.missingRadios?.forEach(r => allMissingRadios.add(r));
    });

    // Criteria that would help differentiate (present in one but not others)
    allMissingSigns.forEach(sign => {
      const presentIn = allClose.filter(d => d.matchedSigns?.includes(sign)).map(d => d.nom);
      const missingIn = allClose.filter(d => d.missingSigns?.includes(sign)).map(d => d.nom);
      if (presentIn.length > 0 && missingIn.length > 0) {
        discriminatingTests.push({
          type: 'signe',
          id: sign,
          name: signes[sign]?.nom,
          helpsDifferentiate: `Présent pour: ${presentIn.join(', ')} | Absent pour: ${missingIn.join(', ')}`
        });
      }
    });

    allMissingRadios.forEach(radio => {
      const presentIn = allClose.filter(d => d.matchedRadios?.includes(radio)).map(d => d.nom);
      const missingIn = allClose.filter(d => d.missingRadios?.includes(radio)).map(d => d.nom);
      if (presentIn.length > 0 && missingIn.length > 0) {
        discriminatingTests.push({
          type: 'radio',
          id: radio,
          name: radios[radio]?.nom,
          helpsDifferentiate: `Présent pour: ${presentIn.join(', ')} | Absent pour: ${missingIn.join(', ')}`
        });
      }
    });

    // Suggest best discriminating tests based on what's missing
    const suggestedTests = [];
    allMissingSigns.forEach(sign => {
      if (!selectedSignes.includes(sign)) {
        suggestedTests.push({ type: 'signe', id: sign, name: signes[sign]?.nom });
      }
    });
    allMissingRadios.forEach(radio => {
      if (!selectedRadios.includes(radio)) {
        suggestedTests.push({ type: 'radio', id: radio, name: radios[radio]?.nom });
      }
    });

    return {
      topDiagnosis: top1,
      closeResults,
      scoreDifference: top1.score - closeResults[0].score,
      discriminatingTests: discriminatingTests.slice(0, 5),
      suggestedTests: suggestedTests.slice(0, 6)
    };
  }, [diagnosticResults, selectedSignes, selectedRadios]);

  const filteredSymptomes = useMemo(() => {
    let items = Object.values(symptomes);
    if (searchTerm) {
      items = items.filter(s => s.nom.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    if (categoryFilter) {
      items = items.filter(s => s.cat === categoryFilter);
    }
    return items;
  }, [searchTerm, categoryFilter]);

  const filteredSignes = useMemo(() => {
    let items = Object.values(signes);
    if (searchTerm) {
      items = items.filter(s => s.nom.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    if (categoryFilter) {
      items = items.filter(s => s.cat === categoryFilter);
    }
    return items;
  }, [searchTerm, categoryFilter]);

  const filteredRadios = useMemo(() => {
    let items = Object.values(radios);
    if (searchTerm) {
      items = items.filter(r => r.nom.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    return items;
  }, [searchTerm]);

  const totalSelected = selectedSymptomes.length + selectedSignes.length + selectedRadios.length;

  const availableCategories = useMemo(() => {
    if (activeTab === 'symptomes') {
      return Object.entries(symptomeCategories);
    } else if (activeTab === 'signes') {
      return Object.entries(signeCategories);
    }
    return [];
  }, [activeTab]);

  // Urgence stats
  const maxUrgence = diagnosticResults.length > 0
    ? Math.max(...diagnosticResults.map(d => d.urgence))
    : 0;

  // Generate console summary text
  const consoleSummary = useMemo(() => {
    if (totalSelected === 0 && diagnosticResults.length === 0) return '';

    const lines = [];
    const date = new Date().toLocaleDateString('fr-FR');

    // Header
    if (patientInfo.nom || patientInfo.prenom) {
      lines.push(`Patient: ${patientInfo.prenom} ${patientInfo.nom}`.trim());
    }
    lines.push(`Date: ${date}`);
    if (patientInfo.praticien) {
      lines.push(`Praticien: ${patientInfo.praticien}`);
    }
    lines.push('');

    // Symptoms
    if (selectedSymptomes.length > 0) {
      lines.push('SYMPTÔMES:');
      selectedSymptomes.forEach(id => {
        const s = symptomes[id];
        if (s) lines.push(`  • ${s.nom}`);
      });
      lines.push('');
    }

    // Clinical signs
    if (selectedSignes.length > 0) {
      lines.push('SIGNES CLINIQUES:');
      selectedSignes.forEach(id => {
        const s = signes[id];
        if (s) lines.push(`  • ${s.nom}`);
      });
      lines.push('');
    }

    // Radio signs
    if (selectedRadios.length > 0) {
      lines.push('SIGNES RADIOLOGIQUES:');
      selectedRadios.forEach(id => {
        const r = radios[id];
        if (r) lines.push(`  • ${r.nom}`);
      });
      lines.push('');
    }

    // Diagnostic hypotheses
    if (diagnosticResults.length > 0) {
      lines.push('HYPOTHÈSES DIAGNOSTIQUES:');
      diagnosticResults.forEach((diag, index) => {
        const urgInfo = urgenceConfig[diag.urgence];
        lines.push(`  ${index + 1}. ${diag.nom} (${diag.score}%) - ${urgInfo?.label || ''}`);
        if (diag.treatments && diag.treatments.length > 0) {
          lines.push(`     Prise en charge: ${diag.treatments.slice(0, 2).join(', ')}`);
        }
      });
    }

    return lines.join('\n');
  }, [selectedSymptomes, selectedSignes, selectedRadios, diagnosticResults, patientInfo]);

  // Copy to clipboard function
  const copyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(consoleSummary);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [consoleSummary]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-sky-50">
      {/* Disclaimer Modal - Shows on each new session */}
      {!disclaimerAccepted && <DisclaimerModal onAccept={handleAcceptDisclaimer} />}

      {/* Header - Style HelloParo */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between flex-wrap gap-4">
            {/* Logo */}
            <LogoSVG />

            {/* Patient Info */}
            <div className="flex items-center gap-2 flex-wrap">
              <input
                type="text"
                placeholder="Nom"
                value={patientInfo.nom}
                onChange={(e) => setPatientInfo({ ...patientInfo, nom: e.target.value })}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-500 focus:border-transparent w-32"
              />
              <input
                type="text"
                placeholder="Prénom"
                value={patientInfo.prenom}
                onChange={(e) => setPatientInfo({ ...patientInfo, prenom: e.target.value })}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-500 focus:border-transparent w-32"
              />
              <div className="flex items-center gap-1 px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
                <Calendar className="w-4 h-4 text-slate-400" />
                <input
                  type="date"
                  value={patientInfo.date}
                  onChange={(e) => setPatientInfo({ ...patientInfo, date: e.target.value })}
                  className="border-none text-sm focus:ring-0 p-0 bg-transparent"
                />
              </div>
              <input
                type="text"
                placeholder="Praticien"
                value={patientInfo.praticien}
                onChange={(e) => setPatientInfo({ ...patientInfo, praticien: e.target.value })}
                className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-500 focus:border-transparent w-44"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {totalSelected > 0 && (
                <button
                  onClick={resetAll}
                  className="flex items-center gap-1 px-3 py-2 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-colors shadow-md"
                >
                  <RotateCcw className="w-4 h-4" />
                  Réinitialiser
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Stats Bar */}
      <div className="bg-white border-b border-slate-200 py-3">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-6 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Symptômes:</span>
              <span className={`font-bold ${selectedSymptomes.length > 0 ? 'text-sky-600' : 'text-slate-400'}`}>
                {selectedSymptomes.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Signes cliniques:</span>
              <span className={`font-bold ${selectedSignes.length > 0 ? 'text-purple-600' : 'text-slate-400'}`}>
                {selectedSignes.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Signes radio:</span>
              <span className={`font-bold ${selectedRadios.length > 0 ? 'text-teal-600' : 'text-slate-400'}`}>
                {selectedRadios.length}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Hypothèses:</span>
              <span className={`font-bold ${diagnosticResults.length > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                {diagnosticResults.length}
              </span>
            </div>
            {maxUrgence >= 4 && (
              <div className="flex items-center gap-2 ml-auto">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                <span className="text-sm font-bold text-red-600">
                  {urgenceConfig[maxUrgence]?.label}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`max-w-7xl mx-auto px-4 py-4 ${totalSelected > 0 ? 'pb-24' : ''}`}>
        {/* Empty State - Guide user to add elements */}
        {totalSelected === 0 && diagnosticResults.length === 0 && activeTab !== 'photos' && (
          <div className="bg-gradient-to-r from-sky-50 to-indigo-50 rounded-xl border border-sky-200 p-6 mb-4">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-6 h-6 text-sky-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-slate-800 mb-2">
                  Comment utiliser HelloDiagnostic ?
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  Pour obtenir des hypothèses diagnostiques, sélectionnez au moins un élément parmi :
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div className="bg-white rounded-lg p-3 border border-sky-200">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-6 h-6 rounded-full bg-sky-500 text-white flex items-center justify-center text-xs font-bold">1</span>
                      <span className="font-medium text-slate-700">Symptômes</span>
                    </div>
                    <p className="text-xs text-slate-500">Douleur, sensibilité, gonflement...</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-purple-200">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-6 h-6 rounded-full bg-purple-500 text-white flex items-center justify-center text-xs font-bold">2</span>
                      <span className="font-medium text-slate-700">Signes cliniques</span>
                    </div>
                    <p className="text-xs text-slate-500">Percussion, palpation, tests...</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-teal-200">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-6 h-6 rounded-full bg-teal-500 text-white flex items-center justify-center text-xs font-bold">3</span>
                      <span className="font-medium text-slate-700">Signes radiologiques</span>
                    </div>
                    <p className="text-xs text-slate-500">Lésions apicales, pertes osseuses...</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-indigo-200">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-xs font-bold">4</span>
                      <span className="font-medium text-slate-700">Pathologies Muqueuses</span>
                    </div>
                    <p className="text-xs text-slate-500">Photo + Analyse IA des lésions buccales</p>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
                  <Target className="w-3 h-3" />
                  Les hypothèses diagnostiques apparaîtront automatiquement ci-dessus.
                </p>
                <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  Pour les lésions muqueuses, importez une photo dans l'onglet dédié et lancez l'analyse IA.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Dynamic Diagnostic Results - Above Tabs (hidden for Pathologies Muqueuses) */}
        {diagnosticResults.length > 0 && activeTab !== "photos" && (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 mb-4 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Target className="w-5 h-5 text-emerald-500" />
                Hypothèses diagnostiques
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                  {diagnosticResults.length}
                </span>
              </h3>
              <div className="flex items-center gap-2">
                {maxUrgence >= 4 && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    <span className="text-sm font-bold text-red-600">
                      {urgenceConfig[maxUrgence]?.label}
                    </span>
                  </div>
                )}
                <button
                  onClick={() => setShowPDFModal(true)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-sky-500 to-violet-500 text-white rounded-lg hover:from-sky-600 hover:to-violet-600 transition-colors text-sm font-medium shadow-sm"
                >
                  <FileDown className="w-4 h-4" />
                  PDF
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {diagnosticResults.map((diag) => {
                const isExpanded = expandedDiag === diag.id;
                const urgInfo = urgenceConfig[diag.urgence];

                return (
                  <div
                    key={diag.id}
                    className={`border rounded-lg overflow-hidden transition-all ${
                      diag.score >= 80 ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200 bg-white'
                    }`}
                  >
                    <button
                      onClick={() => setExpandedDiag(isExpanded ? null : diag.id)}
                      className="w-full p-3 flex items-center gap-3 text-left"
                    >
                      {/* Score */}
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0 ${
                        diag.score >= 80 ? 'bg-emerald-500' : diag.score >= 60 ? 'bg-amber-500' : 'bg-slate-400'
                      }`}>
                        {diag.score}%
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-slate-800 text-sm truncate">{diag.nom}</h4>
                          {diag.fullMatch && (
                            <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-medium flex-shrink-0">
                              Match
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                            diag.urgence >= 5 ? 'bg-red-100 text-red-700' :
                            diag.urgence >= 4 ? 'bg-orange-100 text-orange-700' :
                            diag.urgence >= 3 ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {urgInfo?.label}
                          </span>
                          <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-xs">
                            {diag.cat}
                          </span>
                          {diag.affectedTeeth && diag.affectedTeeth.length > 0 && (
                            <span className="px-1.5 py-0.5 bg-sky-100 text-sky-700 rounded text-xs font-medium flex items-center gap-1">
                              <Target className="w-3 h-3" />
                              {diag.affectedTeeth.length === 32
                                ? 'Global'
                                : diag.affectedTeeth.length <= 3
                                  ? diag.affectedTeeth.join(', ')
                                  : `${diag.affectedTeeth.length} dents`
                              }
                            </span>
                          )}
                        </div>
                      </div>

                      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>

                    {isExpanded && (
                      <div className="px-3 pb-3 border-t border-slate-100 pt-2 space-y-2 animate-fade-in">
                        <p className="text-xs text-slate-500">{diag.desc}</p>

                        {/* Localisation - Dents concernées */}
                        {diag.affectedTeeth && diag.affectedTeeth.length > 0 && (
                          <div className="bg-sky-50 border border-sky-200 rounded-lg p-2">
                            <h5 className="text-xs font-semibold text-sky-700 uppercase mb-1 flex items-center gap-1">
                              <Target className="w-3 h-3" />
                              Localisation
                            </h5>
                            <div className="flex flex-wrap gap-1">
                              {diag.affectedTeeth.length === 32 ? (
                                <span className="px-2 py-0.5 bg-sky-100 text-sky-700 rounded text-xs font-medium">
                                  Global (bouche entière)
                                </span>
                              ) : (
                                diag.affectedTeeth.sort((a, b) => parseInt(a) - parseInt(b)).map(tooth => (
                                  <span key={tooth} className="px-1.5 py-0.5 bg-sky-100 text-sky-700 rounded text-xs font-medium">
                                    {tooth}
                                  </span>
                                ))
                              )}
                            </div>
                          </div>
                        )}

                        {/* Matched criteria */}
                        <div>
                          <h5 className="text-xs font-semibold text-slate-500 uppercase mb-1 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3 text-emerald-500" />
                            Critères
                          </h5>
                          <div className="flex flex-wrap gap-1">
                            {diag.matchedSyms.map(id => (
                              <span key={id} className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs">
                                {symptomes[id]?.icone} {symptomes[id]?.nom}
                              </span>
                            ))}
                            {diag.matchedSigns.map(id => (
                              <span key={id} className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs">
                                {signes[id]?.nom}
                              </span>
                            ))}
                            {diag.matchedRadios.map(id => (
                              <span key={id} className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs">
                                {radios[id]?.nom}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Missing criteria */}
                        {(diag.missingSyms.length > 0 || diag.missingSigns.length > 0 || diag.missingRadios.length > 0) && (
                          <div>
                            <h5 className="text-xs font-semibold text-slate-500 uppercase mb-1 flex items-center gap-1">
                              <Lightbulb className="w-3 h-3 text-amber-500" />
                              À vérifier
                            </h5>
                            <div className="flex flex-wrap gap-1">
                              {diag.missingSyms.map(id => (
                                <button
                                  key={id}
                                  onClick={(e) => { e.stopPropagation(); handleAddCriteria('symptome', id); }}
                                  className="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-xs hover:bg-amber-100 transition-colors"
                                >
                                  + {symptomes[id]?.nom}
                                </button>
                              ))}
                              {diag.missingSigns.map(id => (
                                <button
                                  key={id}
                                  onClick={(e) => { e.stopPropagation(); handleAddCriteria('signe', id); }}
                                  className="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-xs hover:bg-amber-100 transition-colors"
                                >
                                  + {signes[id]?.nom}
                                </button>
                              ))}
                              {diag.missingRadios.map(id => (
                                <button
                                  key={id}
                                  onClick={(e) => { e.stopPropagation(); handleAddCriteria('radio', id); }}
                                  className="px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-xs hover:bg-amber-100 transition-colors"
                                >
                                  + {radios[id]?.nom}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Treatments */}
                        {diag.treatments && diag.treatments.length > 0 && (
                          <div className="bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-lg p-2">
                            <h5 className="text-xs font-semibold text-violet-700 uppercase mb-1 flex items-center gap-1">
                              <BookOpen className="w-3 h-3" />
                              Conduite à tenir
                              {diag.affectedTeeth && diag.affectedTeeth.length > 0 && diag.affectedTeeth.length < 32 && (
                                <span className="ml-1 font-normal normal-case text-violet-600">
                                  - Dent(s): {diag.affectedTeeth.sort((a, b) => parseInt(a) - parseInt(b)).join(', ')}
                                </span>
                              )}
                            </h5>
                            <ul className="space-y-0.5">
                              {diag.treatments.slice(0, 4).map((t, i) => (
                                <li key={i} className="text-xs text-slate-700 flex items-start gap-1">
                                  <span className="text-violet-500">•</span>
                                  {t}
                                </li>
                              ))}
                              {diag.treatments.length > 4 && (
                                <li className="text-xs text-slate-400">
                                  +{diag.treatments.length - 4} autres...
                                </li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Differential Diagnosis Panel (hidden for Pathologies Muqueuses) */}
        {differentialInfo && activeTab !== "photos" && (
          <div className="bg-amber-50 rounded-lg shadow-sm border border-amber-200 p-4 mb-4 animate-fade-in">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <h3 className="text-sm font-semibold text-amber-800">
                Diagnostic différentiel requis
              </h3>
              <span className="px-2 py-0.5 bg-amber-200 text-amber-800 rounded-full text-xs font-medium">
                Δ ≤ {differentialInfo.scoreDifference} pts
              </span>
            </div>

            <p className="text-xs text-amber-700 mb-3">
              <strong>{differentialInfo.topDiagnosis.nom}</strong> ({differentialInfo.topDiagnosis.score}%) et{' '}
              <strong>{differentialInfo.closeResults.map(d => d.nom).join(', ')}</strong> ({differentialInfo.closeResults.map(d => d.score + '%').join(', ')}) ont des scores proches.
              Réalisez les tests suivants pour affiner le diagnostic :
            </p>

            {differentialInfo.suggestedTests.length > 0 && (
              <div className="mb-3">
                <h4 className="text-xs font-semibold text-amber-700 uppercase mb-2 flex items-center gap-1">
                  <Stethoscope className="w-3 h-3" />
                  Tests discriminants suggérés
                </h4>
                <div className="flex flex-wrap gap-2">
                  {differentialInfo.suggestedTests.map((test, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        if (test.type === 'signe') {
                          handleAddCriteria('signe', test.id);
                        } else if (test.type === 'radio') {
                          handleAddCriteria('radio', test.id);
                        }
                      }}
                      className="px-2.5 py-1.5 bg-white border border-amber-300 text-amber-800 rounded-lg text-xs font-medium hover:bg-amber-100 transition-colors flex items-center gap-1.5"
                    >
                      <span className="text-amber-500">+</span>
                      {test.name}
                      <span className="text-[10px] text-amber-500 uppercase">
                        {test.type === 'signe' ? 'clinique' : 'radio'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {differentialInfo.discriminatingTests.length > 0 && (
              <div className="text-xs text-amber-600 bg-amber-100/50 rounded p-2">
                <strong>Indices clés :</strong>
                <ul className="mt-1 space-y-0.5">
                  {differentialInfo.discriminatingTests.slice(0, 3).map((test, idx) => (
                    <li key={idx}>• {test.name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Console Summary (hidden for Pathologies Muqueuses) */}
        {consoleSummary && diagnosticResults.length > 0 && activeTab !== "photos" && (
          <div className="bg-slate-900 rounded-lg shadow-lg border border-slate-700 mb-4 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 bg-slate-800 border-b border-slate-700">
              <div className="flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                </div>
                <span className="text-slate-400 text-xs font-mono ml-2">Résumé pour dossier médical</span>
              </div>
              <button
                onClick={copyToClipboard}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all ${
                  copied
                    ? 'bg-emerald-600 text-white'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                {copied ? (
                  <>
                    <Check className="w-3.5 h-3.5" />
                    Copié !
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" />
                    Copier
                  </>
                )}
              </button>
            </div>
            <pre className="p-4 text-sm text-green-400 font-mono whitespace-pre-wrap overflow-x-auto max-h-40 overflow-y-auto">
              {consoleSummary}
            </pre>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex items-center gap-2 mb-4">
          <div className="flex items-center">
            <button
              onClick={() => { setActiveTab("symptomes"); setCategoryFilter(null); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === "symptomes"
                  ? 'bg-sky-500 text-white shadow-md'
                  : 'bg-white text-slate-600 hover:bg-slate-100'
              }`}
            >
              Symptômes {selectedSymptomes.length > 0 && `(${selectedSymptomes.length})`}
            </button>
            {selectedSymptomes.length > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); resetSymptomes(); }}
                className="ml-1 p-1.5 text-slate-400 hover:text-sky-500 hover:bg-sky-50 rounded-full transition-all"
                title="Réinitialiser les symptômes"
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="flex items-center">
            <button
              onClick={() => { setActiveTab("signes"); setCategoryFilter(null); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === "signes"
                  ? 'bg-purple-500 text-white shadow-md'
                  : 'bg-white text-slate-600 hover:bg-slate-100'
              }`}
            >
              Signes cliniques {selectedSignes.length > 0 && `(${selectedSignes.length})`}
            </button>
            {selectedSignes.length > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); resetSignes(); }}
                className="ml-1 p-1.5 text-slate-400 hover:text-purple-500 hover:bg-purple-50 rounded-full transition-all"
                title="Réinitialiser les signes cliniques"
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <div className="flex items-center">
            <button
              onClick={() => { setActiveTab("radios"); setCategoryFilter(null); }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === "radios"
                  ? 'bg-teal-500 text-white shadow-md'
                  : 'bg-white text-slate-600 hover:bg-slate-100'
              }`}
            >
              Radiologie {selectedRadios.length > 0 && `(${selectedRadios.length})`}
            </button>
            {selectedRadios.length > 0 && (
              <button
                onClick={(e) => { e.stopPropagation(); resetRadios(); }}
                className="ml-1 p-1.5 text-slate-400 hover:text-teal-500 hover:bg-teal-50 rounded-full transition-all"
                title="Réinitialiser les radios"
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Separator */}
          <div className="h-8 w-px bg-slate-300 mx-2"></div>

          <button
            onClick={() => { setActiveTab("photos"); setCategoryFilter(null); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === "photos"
                ? 'bg-indigo-500 text-white shadow-md'
                : 'bg-white text-slate-600 hover:bg-slate-100 border border-indigo-200'
            }`}
          >
            <span className="flex items-center gap-1.5">
              <Sparkles className="w-4 h-4" />
              Pathologies Muqueuses {photos.length > 0 && `(${photos.length})`}
            </span>
          </button>
        </div>

        {/* Photos Panel */}
        {activeTab === "photos" && (
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-indigo-500" />
                Pathologies Muqueuses - Analyse IA
              </h2>
              <button
                onClick={() => setShowReferentielModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-200 transition-colors"
              >
                <HelpCircle className="w-4 h-4" />
                Guide du référentiel
              </button>
            </div>

            {/* Section QR Code */}
            <div className="mb-6 p-6 bg-indigo-50 rounded-xl border border-indigo-200">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-indigo-900 mb-2">
                    Ajouter une photo depuis votre téléphone
                  </h3>
                  <p className="text-sm text-indigo-700 mb-4">
                    Scannez le QR code avec votre téléphone pour prendre une photo qui sera automatiquement ajoutée au dossier patient.
                  </p>

                  {peerStatus === 'idle' && (
                    <button
                      onClick={async () => {
                        setPeerStatus('waiting');
                        const peer = new Peer();
                        peerRef.current = peer;

                        peer.on('open', async (id) => {
                          const url = `${window.location.origin}${window.location.pathname}?photo=1&peer=${id}`;
                          const qrDataUrl = await QRCode.toDataURL(url, {
                            width: 200,
                            margin: 2,
                            color: { dark: '#4f46e5', light: '#ffffff' }
                          });
                          setQrCodeUrl(qrDataUrl);
                        });

                        peer.on('connection', (conn) => {
                          connRef.current = conn;
                          setPeerStatus('connected');

                          conn.on('data', (data) => {
                            if (data.type === 'photo') {
                              const newPhoto = {
                                id: Date.now(),
                                data: data.data,
                                name: `Photo ${photos.length + 1}`,
                                date: new Date().toLocaleDateString('fr-FR')
                              };
                              setPhotos(prev => [...prev, newPhoto]);
                            }
                          });

                          conn.on('close', () => {
                            setPeerStatus('waiting');
                          });
                        });

                        peer.on('error', (err) => {
                          console.error('Peer error:', err);
                          setPeerStatus('idle');
                          setQrCodeUrl(null);
                        });
                      }}
                      className="px-4 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2"
                    >
                      <Camera className="w-5 h-5" />
                      Générer un QR Code
                    </button>
                  )}

                  {peerStatus === 'waiting' && (
                    <div className="flex items-center gap-2 text-indigo-600">
                      <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span className="font-medium">En attente de connexion...</span>
                    </div>
                  )}

                  {peerStatus === 'connected' && (
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-medium">Téléphone connecté !</span>
                    </div>
                  )}
                </div>

                {qrCodeUrl && (
                  <div className="flex flex-col items-center">
                    <img src={qrCodeUrl} alt="QR Code" className="w-40 h-40 rounded-lg shadow-md" />
                    <button
                      onClick={() => {
                        if (peerRef.current) peerRef.current.destroy();
                        if (connRef.current) connRef.current.close();
                        setPeerStatus('idle');
                        setQrCodeUrl(null);
                      }}
                      className="mt-2 text-sm text-slate-500 hover:text-slate-700"
                    >
                      Fermer
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Section import local */}
            <div className="mb-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-slate-700 mb-1">
                    Importer depuis votre appareil
                  </h3>
                  <p className="text-xs text-slate-500">
                    Sélectionnez des photos depuis votre ordinateur ou appareil
                  </p>
                </div>
                <label className="px-4 py-2.5 bg-slate-600 text-white rounded-lg font-medium hover:bg-slate-700 transition-colors flex items-center gap-2 cursor-pointer">
                  <Upload className="w-5 h-5" />
                  Choisir des photos
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      files.forEach((file, idx) => {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          const newPhoto = {
                            id: Date.now() + idx,
                            data: event.target.result,
                            name: file.name.replace(/\.[^/.]+$/, '') || `Photo ${photos.length + idx + 1}`,
                            date: new Date().toLocaleDateString('fr-FR')
                          };
                          setPhotos(prev => [...prev, newPhoto]);
                        };
                        reader.readAsDataURL(file);
                      });
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            </div>

            {/* Grille des photos */}
            {photos.length > 0 ? (
              <div className="space-y-6">
                {photos.map((photo, index) => {
                  const analysis = photoAnalysis[photo.id];
                  const isAnalyzing = analysis?.status === 'analyzing';
                  const hasResults = analysis?.status === 'done' && analysis.result?.diagnostic?.hypotheses?.length > 0;
                  const hasError = analysis?.status === 'error';

                  return (
                    <div key={photo.id} className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden">
                      <div className="flex flex-col md:flex-row">
                        {/* Image */}
                        <div className="relative md:w-64 flex-shrink-0">
                          <div className="aspect-square md:aspect-auto md:h-full bg-slate-100">
                            <img
                              src={photo.data}
                              alt={photo.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          {/* Delete button */}
                          <button
                            onClick={() => {
                              if (confirm('Supprimer cette photo ?')) {
                                setPhotos(prev => prev.filter(p => p.id !== photo.id));
                                // Clean up analysis state too
                                setPhotoAnalysis(prev => {
                                  const newState = { ...prev };
                                  delete newState[photo.id];
                                  return newState;
                                });
                              }
                            }}
                            className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>

                          {/* Analyzing overlay */}
                          {isAnalyzing && (
                            <div className="absolute inset-0 bg-indigo-900/80 flex flex-col items-center justify-center">
                              <div className="relative">
                                <div className="w-16 h-16 border-4 border-indigo-300 border-t-indigo-500 rounded-full animate-spin"></div>
                                <Sparkles className="w-6 h-6 text-indigo-300 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                              </div>
                              <p className="text-white font-medium mt-4">Analyse en cours...</p>
                              <p className="text-indigo-300 text-sm">Vision IA</p>
                            </div>
                          )}
                        </div>

                        {/* Info and Analysis */}
                        <div className="flex-1 p-4">
                          {/* Photo info */}
                          <div className="flex items-start justify-between mb-4">
                            <div>
                              <input
                                type="text"
                                value={photo.name}
                                onChange={(e) => {
                                  const updated = [...photos];
                                  updated[index].name = e.target.value;
                                  setPhotos(updated);
                                }}
                                className="text-lg font-semibold text-slate-800 bg-transparent border-b border-transparent hover:border-slate-300 focus:border-indigo-500 focus:outline-none"
                              />
                              <p className="text-sm text-slate-500">{photo.date}</p>
                            </div>

                            {/* Analyze button */}
                            {!isAnalyzing && !hasResults && (
                              <button
                                onClick={() => analyzePhoto(photo.id, photo.data)}
                                className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg font-medium hover:from-indigo-600 hover:to-purple-600 transition-all shadow-md hover:shadow-lg flex items-center gap-2"
                              >
                                <Sparkles className="w-4 h-4" />
                                Analyse IA
                              </button>
                            )}

                            {isAnalyzing && (
                              <div className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg font-medium flex items-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Analyse...
                              </div>
                            )}

                            {hasResults && (
                              <button
                                onClick={() => analyzePhoto(photo.id, photo.data)}
                                className="px-3 py-1.5 bg-slate-200 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-300 transition-colors flex items-center gap-1"
                              >
                                <RotateCcw className="w-3 h-3" />
                                Réanalyser
                              </button>
                            )}
                          </div>

                          {/* Error state */}
                          {hasError && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-4">
                              <div className="flex items-center gap-2 text-red-700">
                                <AlertCircle className="w-4 h-4" />
                                <span className="font-medium">Erreur d'analyse</span>
                              </div>
                              <p className="text-sm text-red-600 mt-1">{analysis.error || 'Une erreur est survenue'}</p>
                              <button
                                onClick={() => analyzePhoto(photo.id, photo.data)}
                                className="mt-2 px-3 py-1 bg-red-100 text-red-700 rounded text-sm font-medium hover:bg-red-200"
                              >
                                Réessayer
                              </button>
                            </div>
                          )}

                          {/* Analysis Results */}
                          {hasResults && (
                            <div className="space-y-4">
                              {/* Root-level critical malignancy alert (V2) */}
                              {analysis.result?.alerte && (
                                <div className="p-4 bg-red-100 border-2 border-red-400 rounded-lg animate-pulse">
                                  <div className="flex items-start gap-3">
                                    <div className="p-2 bg-red-500 rounded-full">
                                      <AlertTriangle className="w-5 h-5 text-white" />
                                    </div>
                                    <div className="flex-1">
                                      <h4 className="text-red-800 font-bold text-lg flex items-center gap-2">
                                        {analysis.result.alerte.type || 'ALERTE CRITIQUE'}
                                        {analysis.result.alerte.severite && (
                                          <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full uppercase">
                                            {analysis.result.alerte.severite}
                                          </span>
                                        )}
                                      </h4>
                                      {analysis.result.alerte.message && (
                                        <p className="text-red-700 font-medium mt-1">{analysis.result.alerte.message}</p>
                                      )}
                                      {analysis.result.alerte.action && (
                                        <p className="text-red-600 text-sm mt-2 flex items-center gap-1">
                                          <ChevronRight className="w-4 h-4" />
                                          <strong>Action:</strong> {analysis.result.alerte.action}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Overall urgency and action */}
                              {analysis.result?.diagnostic?.urgence && (() => {
                                // Handle urgence as object or string
                                const urgence = analysis.result.diagnostic.urgence;
                                const niveau = typeof urgence === 'object' ? urgence.niveau : (urgence >= 1 && urgence <= 5 ? urgence : 2);
                                const label = typeof urgence === 'object' ? urgence.label : (
                                  niveau >= 5 ? 'Très urgent' :
                                  niveau >= 4 ? 'Urgent' :
                                  niveau >= 3 ? 'Assez urgent' :
                                  niveau >= 2 ? 'Peu urgent' : 'Non urgent'
                                );
                                return (
                                  <div className={`p-3 rounded-lg border ${
                                    niveau >= 4 ? 'bg-red-50 border-red-200' :
                                    niveau >= 3 ? 'bg-orange-50 border-orange-200' :
                                    'bg-blue-50 border-blue-200'
                                  }`}>
                                    <div className="flex items-center justify-between flex-wrap gap-2">
                                      <div className="flex items-center gap-2">
                                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                                          niveau >= 4 ? 'bg-red-500 text-white' :
                                          niveau >= 3 ? 'bg-orange-500 text-white' :
                                          'bg-blue-500 text-white'
                                        }`}>
                                          {label}
                                        </span>
                                        {/* Alerte V3 ou V4 */}
                                        {(analysis.result.diagnostic.alerte_malignite || analysis.result.alerte) && (
                                          <span className="px-2 py-0.5 bg-red-600 text-white text-xs font-bold rounded-full animate-pulse">
                                            ⚠️ {analysis.result.alerte?.message || 'SUSPICION MALIGNITÉ'}
                                          </span>
                                        )}
                                        {/* Action recommandée V3 ou V4 */}
                                        {(analysis.result.diagnostic.action_recommandee || analysis.result.diagnostic.action) && (
                                          <span className="text-sm text-slate-600">
                                            → {analysis.result.diagnostic.action_recommandee || analysis.result.diagnostic.action}
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {/* Red flags count V3 (nombre_red_flags) ou V4 (nb_red_flags) */}
                                        {(analysis.result.diagnostic?.nombre_red_flags > 0 || analysis.result.diagnostic?.nb_red_flags > 0) && (() => {
                                          const nbFlags = analysis.result.diagnostic.nombre_red_flags || analysis.result.diagnostic.nb_red_flags;
                                          const nbCritiques = analysis.result.diagnostic.red_flags_critiques ||
                                            analysis.result.diagnostic.red_flags?.filter(f => f.severity === 'critique').length || 0;
                                          return (
                                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                              nbCritiques > 0 ? 'bg-red-200 text-red-700' : 'bg-amber-200 text-amber-700'
                                            }`}>
                                              {nbFlags} red flag{nbFlags > 1 ? 's' : ''}
                                              {nbCritiques > 0 && ` (${nbCritiques} critique${nbCritiques > 1 ? 's' : ''})`}
                                            </span>
                                          );
                                        })()}
                                        {analysis.result.meta?.pathologies_evaluees && (
                                          <span className="text-xs text-slate-500">
                                            {analysis.result.meta.pathologies_evaluees} pathologies évaluées
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Summary from API */}
                              {analysis.result?.analyse?.resume && (
                                <div className="p-3 bg-slate-100 rounded-lg">
                                  <h4 className="text-xs font-semibold text-slate-500 uppercase mb-1">Résumé sémiologique</h4>
                                  <p className="text-sm text-slate-700">{analysis.result.analyse.resume}</p>
                                </div>
                              )}

                              {/* Score de confiance V4 */}
                              {analysis.result?.validation?.score_confiance !== undefined && (
                                <div className="p-3 bg-white border border-slate-200 rounded-lg">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <h4 className="text-xs font-semibold text-slate-500 uppercase">Score de confiance</h4>
                                      <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                                        analysis.result.validation.score_confiance >= 80 ? 'bg-emerald-100 text-emerald-700' :
                                        analysis.result.validation.score_confiance >= 60 ? 'bg-amber-100 text-amber-700' :
                                        'bg-red-100 text-red-700'
                                      }`}>
                                        {analysis.result.validation.score_confiance}% - {analysis.result.validation.score_confiance_label || (
                                          analysis.result.validation.score_confiance >= 80 ? 'Élevée' :
                                          analysis.result.validation.score_confiance >= 60 ? 'Moyenne' : 'Faible'
                                        )}
                                      </span>
                                    </div>
                                    {/* Progress bar */}
                                    <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                                      <div
                                        className={`h-full rounded-full transition-all ${
                                          analysis.result.validation.score_confiance >= 80 ? 'bg-emerald-500' :
                                          analysis.result.validation.score_confiance >= 60 ? 'bg-amber-500' :
                                          'bg-red-500'
                                        }`}
                                        style={{ width: `${analysis.result.validation.score_confiance}%` }}
                                      />
                                    </div>
                                  </div>
                                  {/* Champs manquants */}
                                  {analysis.result.validation.champs_manquants?.length > 0 && (
                                    <div className="mt-2 text-xs text-slate-500">
                                      <span className="font-medium">Données manquantes : </span>
                                      {analysis.result.validation.champs_manquants.join(', ')}
                                    </div>
                                  )}
                                  {/* Alerte validation */}
                                  {analysis.result.validation.alerte_validation && (
                                    <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                                      ⚠️ {analysis.result.validation.alerte_validation}
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Alertes (red flags from server) */}
                              {analysis.result?.diagnostic?.alertes?.length > 0 && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                  <div className="flex items-center gap-2 text-red-700 font-semibold">
                                    <AlertTriangle className="w-4 h-4" />
                                    Alertes détectées
                                  </div>
                                  <ul className="text-sm text-red-600 mt-1 space-y-0.5">
                                    {analysis.result.diagnostic.alertes.map((alerte, i) => (
                                      <li key={i}>
                                        • {typeof alerte === 'string' ? alerte : (alerte.type || alerte.action || JSON.stringify(alerte))}
                                        {alerte.severite && <span className="ml-1 text-xs">({alerte.severite})</span>}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Red flags V2 format */}
                              {analysis.result?.diagnostic?.red_flags?.length > 0 && (
                                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                  <div className="flex items-center gap-2 text-amber-700 font-semibold mb-2">
                                    <AlertCircle className="w-4 h-4" />
                                    Red flags ({analysis.result.diagnostic.red_flags.length})
                                  </div>
                                  <div className="space-y-2">
                                    {analysis.result.diagnostic.red_flags.map((flag, i) => {
                                      const sev = flag.severite || flag.severity;
                                      const isCritique = flag.critique || sev === 'critique';
                                      return (
                                      <div key={i} className={`p-2 rounded border ${
                                        isCritique ? 'bg-red-50 border-red-300' : 'bg-white border-amber-200'
                                      }`}>
                                        <div className="flex items-start gap-2">
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                              <span className="font-medium text-slate-800 text-sm">
                                                {typeof flag === 'string' ? flag : (flag.type || flag.flag || 'Red flag')}
                                              </span>
                                              {/* Severity V3 (severite) ou V4 (severity) */}
                                              {sev && (
                                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                                                  sev === 'critique' || sev === 'élevée' || sev === 'haute' ? 'bg-red-100 text-red-700' :
                                                  sev === 'modérée' || sev === 'moderee' ? 'bg-orange-100 text-orange-700' :
                                                  'bg-amber-100 text-amber-700'
                                                }`}>
                                                  {sev}
                                                </span>
                                              )}
                                              {isCritique && (
                                                <span className="px-1.5 py-0.5 bg-red-500 text-white rounded text-xs font-bold">
                                                  CRITIQUE
                                                </span>
                                              )}
                                            </div>
                                            {flag.action && (
                                              <p className="text-xs text-slate-600 mt-1 flex items-start gap-1">
                                                <ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                                {flag.action}
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    );})}
                                  </div>
                                </div>
                              )}

                              {/* Examens complémentaires recommandés V4 */}
                              {analysis.result?.diagnostic?.examens_complementaires?.length > 0 && (
                                <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg">
                                  <h4 className="text-sm font-semibold text-sky-700 mb-2 flex items-center gap-2">
                                    <Stethoscope className="w-4 h-4" />
                                    Examens cliniques recommandés
                                  </h4>
                                  <div className="space-y-2">
                                    {analysis.result.diagnostic.examens_complementaires.map((exam, i) => (
                                      <div key={i} className="flex items-start gap-2 p-2 bg-white rounded border border-sky-100">
                                        <div className="w-6 h-6 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                                          <span className="text-xs font-bold text-sky-600">{i + 1}</span>
                                        </div>
                                        <div className="flex-1">
                                          <span className="font-medium text-slate-800 text-sm">{exam.examen}</span>
                                          {exam.raison && (
                                            <p className="text-xs text-slate-500 mt-0.5">{exam.raison}</p>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Diagnostic hypotheses */}
                              <div>
                                <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                                  <Target className="w-4 h-4 text-indigo-500" />
                                  Hypothèses diagnostiques
                                </h4>
                                <div className="space-y-2">
                                  {analysis.result.diagnostic.hypotheses.map((hypo, idx) => {
                                    const urgenceLevel = hypo.urgence || 2;
                                    return (
                                      <div key={idx} className={`p-3 rounded-lg border ${
                                        idx === 0 ? 'bg-indigo-50 border-indigo-200' :
                                        hypo.alerte ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'
                                      }`}>
                                        <div className="flex items-start justify-between gap-2">
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                                                hypo.probabilite >= 70 ? 'bg-emerald-500' : hypo.probabilite >= 50 ? 'bg-amber-500' : 'bg-slate-400'
                                              }`}>
                                                {hypo.probabilite}%
                                              </span>
                                              <h5 className="font-semibold text-slate-800">{hypo.nom}</h5>
                                              {hypo.alerte && (
                                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                                                  ⚠️ Alerte
                                                </span>
                                              )}
                                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                                urgenceLevel >= 4 ? 'bg-red-100 text-red-700' :
                                                urgenceLevel >= 3 ? 'bg-orange-100 text-orange-700' :
                                                'bg-blue-100 text-blue-700'
                                              }`}>
                                                {urgenceLevel >= 4 ? 'Urgent' : urgenceLevel >= 3 ? 'Assez urgent' : 'Peu urgent'}
                                              </span>
                                            </div>
                                            <p className="text-xs text-slate-500 mt-1 capitalize">
                                              {typeof hypo.categorie === 'string'
                                                ? hypo.categorie.replace(/_/g, ' ')
                                                : (hypo.categorie?.nom || hypo.categorie?.label || '')}
                                            </p>
                                            {hypo.description && (
                                              <p className="text-xs text-slate-600 mt-1">{hypo.description}</p>
                                            )}
                                            {/* Conduite à tenir V4 */}
                                            {hypo.conduite && (
                                              <div className="mt-2 p-2 bg-emerald-50 border border-emerald-200 rounded">
                                                <p className="text-xs text-emerald-700 flex items-start gap-1">
                                                  <Clipboard className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                                  <span><strong>Conduite :</strong> {hypo.conduite}</span>
                                                </p>
                                              </div>
                                            )}
                                          </div>
                                        </div>

                                        {/* Matched criteria from server - translated (V3: criteres_correspondants, V4: criteres) */}
                                        {(hypo.criteres_correspondants?.length > 0 || hypo.criteres?.length > 0) && (
                                          <div className="mt-2 flex flex-wrap gap-1">
                                            {(hypo.criteres_correspondants || hypo.criteres)
                                              .map(c => translateCriterion(c))
                                              .filter(t => t && t.trim())
                                              .slice(0, 8)
                                              .map((translated, i) => (
                                                <span key={i} className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs border border-slate-200">
                                                  {translated}
                                                </span>
                                              ))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>

                              {/* Image quality info */}
                              {analysis.result?.analyse?.qualite_image && (
                                <div className="text-xs text-slate-500 flex items-center gap-2">
                                  <span>Qualité image:</span>
                                  <span className={`px-2 py-0.5 rounded font-medium ${
                                    analysis.result.analyse.qualite_image === 'bonne' || analysis.result.analyse.qualite_image === 'excellente'
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : analysis.result.analyse.qualite_image === 'moyenne'
                                      ? 'bg-amber-100 text-amber-700'
                                      : 'bg-red-100 text-red-700'
                                  }`}>
                                    {analysis.result.analyse.qualite_image}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Empty state - no analysis yet */}
                          {!isAnalyzing && !hasResults && !hasError && (
                            <div className="text-center py-4 text-slate-400">
                              <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
                              <p className="text-sm">Cliquez sur "Analyse IA" pour identifier la pathologie muqueuse</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Camera className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="font-medium">Aucune photo</p>
                <p className="text-sm">Importez une photo de lésion muqueuse pour lancer l'analyse IA</p>
              </div>
            )}
          </div>
        )}

        {/* Selection Panel */}
        {activeTab !== "photos" && (
        <div>
          <div className="flex-1">
              {/* Search & Filters */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 mb-4">
                <div className="flex gap-2 mb-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Rechercher..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* Category Filters */}
                {availableCategories.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => setCategoryFilter(null)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        !categoryFilter
                          ? 'bg-slate-800 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      Tous
                    </button>
                    {availableCategories.map(([key, value]) => (
                      <button
                        key={key}
                        onClick={() => setCategoryFilter(key)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                          categoryFilter === key
                            ? 'bg-slate-800 text-white'
                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                        }`}
                      >
                        {value.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Schéma dentaire */}
              <div className="mb-4">
                <DentalChart
                  selectedTeeth={activeTeeth}
                  onSelectionChange={setActiveTeeth}
                  compact={true}
                />
                {activeTeeth.length > 0 && (
                  <p className="text-xs text-center text-slate-500 mt-2">
                    Sélectionnez les éléments cliniques qui s'appliquent à cette localisation
                  </p>
                )}
              </div>

              {/* Items Grid */}
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-[400px] overflow-y-auto">
                  {activeTab === "symptomes" && filteredSymptomes.map(item => (
                    <button
                      key={item.id}
                      onClick={() => toggleItem(item.id, selectedSymptomes, setSelectedSymptomes)}
                      className={`flex items-center gap-2 p-3 rounded-lg border text-left transition-all ${
                        selectedSymptomes.includes(item.id)
                          ? 'bg-sky-50 border-sky-300 text-sky-800'
                          : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <span className="text-lg">{item.icone}</span>
                      <span className="text-sm font-medium flex-1">{item.nom}</span>
                      {itemLocalisations[item.id]?.length > 0 && (
                        <span className="text-xs bg-sky-200 text-sky-700 px-1.5 py-0.5 rounded">
                          {itemLocalisations[item.id].length === 32 ? 'Global' : itemLocalisations[item.id].length + ' dent(s)'}
                        </span>
                      )}
                      {selectedSymptomes.includes(item.id) && (
                        <CheckCircle className="w-4 h-4 text-sky-500" />
                      )}
                    </button>
                  ))}

                  {activeTab === "signes" && filteredSignes.map(item => (
                    <button
                      key={item.id}
                      onClick={() => toggleItem(item.id, selectedSignes, setSelectedSignes)}
                      className={`flex items-center gap-2 p-3 rounded-lg border text-left transition-all ${
                        selectedSignes.includes(item.id)
                          ? 'bg-purple-50 border-purple-300 text-purple-800'
                          : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <Eye className="w-4 h-4 text-slate-400" />
                      <span className="text-sm font-medium flex-1">{item.nom}</span>
                      {itemLocalisations[item.id]?.length > 0 && (
                        <span className="text-xs bg-purple-200 text-purple-700 px-1.5 py-0.5 rounded">
                          {itemLocalisations[item.id].length === 32 ? 'Global' : itemLocalisations[item.id].length + ' dent(s)'}
                        </span>
                      )}
                      {selectedSignes.includes(item.id) && (
                        <CheckCircle className="w-4 h-4 text-purple-500" />
                      )}
                    </button>
                  ))}

                  {activeTab === "radios" && filteredRadios.map(item => (
                    <button
                      key={item.id}
                      onClick={() => toggleItem(item.id, selectedRadios, setSelectedRadios)}
                      className={`flex items-center gap-2 p-3 rounded-lg border text-left transition-all ${
                        selectedRadios.includes(item.id)
                          ? 'bg-teal-50 border-teal-300 text-teal-800'
                          : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <FileText className="w-4 h-4 text-slate-400" />
                      <span className="text-sm font-medium flex-1">{item.nom}</span>
                      {itemLocalisations[item.id]?.length > 0 && (
                        <span className="text-xs bg-teal-200 text-teal-700 px-1.5 py-0.5 rounded">
                          {itemLocalisations[item.id].length === 32 ? 'Global' : itemLocalisations[item.id].length + ' dent(s)'}
                        </span>
                      )}
                      {selectedRadios.includes(item.id) && (
                        <CheckCircle className="w-4 h-4 text-teal-500" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Selection Summary - Bottom Bar */}
        {totalSelected > 0 && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg py-3 px-4">
            <div className="max-w-7xl mx-auto flex items-center gap-4 flex-wrap">
              <span className="text-sm font-medium text-slate-600">Sélection:</span>
              <div className="flex flex-wrap gap-2 flex-1">
                {selectedSymptomes.map(id => (
                  <span key={id} className="inline-flex items-center gap-1 px-2 py-1 bg-sky-100 text-sky-700 rounded text-xs">
                    {symptomes[id]?.icone} {symptomes[id]?.nom}
                    {itemLocalisations[id]?.length > 0 && (
                      <span className="text-sky-500 font-medium">
                        ({itemLocalisations[id].length === 32 ? 'Global' : itemLocalisations[id].join(',')})
                      </span>
                    )}
                    <button onClick={() => toggleItem(id, selectedSymptomes, setSelectedSymptomes)} className="hover:text-sky-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {selectedSignes.map(id => (
                  <span key={id} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                    {signes[id]?.nom}
                    {itemLocalisations[id]?.length > 0 && (
                      <span className="text-purple-500 font-medium">
                        ({itemLocalisations[id].length === 32 ? 'Global' : itemLocalisations[id].join(',')})
                      </span>
                    )}
                    <button onClick={() => toggleItem(id, selectedSignes, setSelectedSignes)} className="hover:text-purple-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                {selectedRadios.map(id => (
                  <span key={id} className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-700 rounded text-xs">
                    {radios[id]?.nom}
                    {itemLocalisations[id]?.length > 0 && (
                      <span className="text-teal-500 font-medium">
                        ({itemLocalisations[id].length === 32 ? 'Global' : itemLocalisations[id].join(',')})
                      </span>
                    )}
                    <button onClick={() => toggleItem(id, selectedRadios, setSelectedRadios)} className="hover:text-teal-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* PDF Modal */}
        <DiagnosticPDF
          isOpen={showPDFModal}
          onClose={() => setShowPDFModal(false)}
          patientInfo={patientInfo}
          diagnosticResults={diagnosticResults}
          selectedSymptomes={selectedSymptomes}
          selectedSignes={selectedSignes}
          selectedRadios={selectedRadios}
          itemLocalisations={itemLocalisations}
        />

        {/* Modal Référentiel Clinique */}
        {showReferentielModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-slate-800">Guide du Référentiel Clinique</h2>
                    <p className="text-xs text-slate-500">Version 4.0 - 35 pathologies</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowReferentielModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-slate-200 px-4 overflow-x-auto">
                {[
                  { id: 'overview', label: 'Vue d\'ensemble', icon: Sparkles },
                  { id: 'categories', label: 'Catégories', icon: Filter },
                  { id: 'urgence', label: 'Urgences', icon: AlertTriangle },
                  { id: 'redflags', label: 'Red Flags', icon: AlertCircle },
                  { id: 'examens', label: 'Examens', icon: Stethoscope },
                  { id: 'codes', label: 'Codes', icon: FileText },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setReferentielTab(tab.id)}
                    className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      referentielTab === tab.id
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <tab.icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Vue d'ensemble */}
                {referentielTab === 'overview' && (
                  <div className="space-y-6">
                    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-100">
                      <h3 className="text-lg font-semibold text-indigo-900 mb-3">Comment ça fonctionne ?</h3>
                      <div className="grid md:grid-cols-3 gap-4">
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center mb-2">
                            <Camera className="w-4 h-4 text-indigo-600" />
                          </div>
                          <h4 className="font-medium text-slate-800 mb-1">1. Photo</h4>
                          <p className="text-xs text-slate-600">Importez une photo de la lésion muqueuse buccale</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mb-2">
                            <Sparkles className="w-4 h-4 text-purple-600" />
                          </div>
                          <h4 className="font-medium text-slate-800 mb-1">2. Analyse IA</h4>
                          <p className="text-xs text-slate-600">L'IA analyse les caractéristiques sémiologiques</p>
                        </div>
                        <div className="bg-white rounded-lg p-4 shadow-sm">
                          <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center mb-2">
                            <Target className="w-4 h-4 text-emerald-600" />
                          </div>
                          <h4 className="font-medium text-slate-800 mb-1">3. Hypothèses</h4>
                          <p className="text-xs text-slate-600">Recevez des hypothèses diagnostiques avec scores</p>
                        </div>
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="bg-slate-50 rounded-lg p-4">
                        <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                          <Activity className="w-4 h-4 text-slate-600" />
                          Statistiques du référentiel
                        </h4>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Pathologies</span>
                            <span className="font-semibold text-slate-800">35</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Catégories</span>
                            <span className="font-semibold text-slate-800">13</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Codes de localisation</span>
                            <span className="font-semibold text-slate-800">27</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-600">Red Flags</span>
                            <span className="font-semibold text-slate-800">9</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                        <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          Avertissement
                        </h4>
                        <p className="text-sm text-amber-700">
                          Cet outil est une <strong>aide au diagnostic</strong> et ne remplace pas l'examen clinique
                          et le jugement du praticien. Une biopsie reste indispensable pour tout diagnostic de certitude.
                        </p>
                      </div>
                    </div>

                    <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                      <h4 className="font-semibold text-emerald-800 mb-2">Score de confiance</h4>
                      <p className="text-sm text-emerald-700 mb-2">
                        Chaque analyse fournit un <strong>score de confiance</strong> basé sur la complétude des données extraites :
                      </p>
                      <div className="flex gap-4 text-xs">
                        <span className="px-2 py-1 bg-emerald-200 text-emerald-800 rounded">≥80% = Élevée</span>
                        <span className="px-2 py-1 bg-amber-200 text-amber-800 rounded">60-79% = Moyenne</span>
                        <span className="px-2 py-1 bg-red-200 text-red-800 rounded">&lt;60% = Faible</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Catégories */}
                {referentielTab === 'categories' && (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600 mb-4">
                      Les 35 pathologies sont organisées en 13 catégories diagnostiques :
                    </p>
                    <div className="grid md:grid-cols-2 gap-3">
                      {[
                        { cat: 'Infection fongique', ex: 'Candidose, Glossite losangique', color: 'bg-orange-100 text-orange-800 border-orange-200' },
                        { cat: 'Infection bactérienne', ex: 'GUN, Syphilis, Tuberculose', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
                        { cat: 'Infection virale', ex: 'Herpès, Zona', color: 'bg-lime-100 text-lime-800 border-lime-200' },
                        { cat: 'Potentiellement malin', ex: 'Leucoplasie, Érythroplasie', color: 'bg-amber-100 text-amber-800 border-amber-200' },
                        { cat: 'Malin', ex: 'Carcinome, Mélanome, Kaposi', color: 'bg-red-100 text-red-800 border-red-200' },
                        { cat: 'Auto-immun', ex: 'Lichen plan, Pemphigus, Lupus', color: 'bg-purple-100 text-purple-800 border-purple-200' },
                        { cat: 'Ulcération bénigne', ex: 'Aphte mineur, Aphte majeur', color: 'bg-blue-100 text-blue-800 border-blue-200' },
                        { cat: 'Tumeur bénigne', ex: 'Fibrome, Mucocèle, Papillome', color: 'bg-cyan-100 text-cyan-800 border-cyan-200' },
                        { cat: 'Bénin', ex: 'Langue géographique', color: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
                        { cat: 'Réactionnelle', ex: 'Morsicatio, Kératose frictionnelle', color: 'bg-slate-100 text-slate-800 border-slate-200' },
                        { cat: 'Médicamenteuse', ex: 'Hyperplasie, ONM, Toxidermie', color: 'bg-pink-100 text-pink-800 border-pink-200' },
                        { cat: 'Granulomatose', ex: 'Sarcoïdose, Crohn buccal', color: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
                        { cat: 'Maladie de dépôt', ex: 'Amylose buccale', color: 'bg-violet-100 text-violet-800 border-violet-200' },
                      ].map((item, i) => (
                        <div key={i} className={`p-3 rounded-lg border ${item.color}`}>
                          <h5 className="font-semibold text-sm">{item.cat}</h5>
                          <p className="text-xs mt-1 opacity-80">{item.ex}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Niveaux d'urgence */}
                {referentielTab === 'urgence' && (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600 mb-4">
                      5 niveaux d'urgence guident la prise en charge :
                    </p>
                    <div className="space-y-3">
                      {[
                        { niveau: 1, label: 'Non urgent', color: 'bg-emerald-500', desc: 'Surveillance simple', action: 'Contrôle lors de la prochaine visite' },
                        { niveau: 2, label: 'Peu urgent', color: 'bg-blue-500', desc: 'Consultation programmée', action: 'Rendez-vous dans le mois' },
                        { niveau: 3, label: 'Urgent', color: 'bg-orange-500', desc: 'Consultation dans les 2 semaines', action: 'Biopsie/traitement à planifier' },
                        { niveau: 4, label: 'Très urgent', color: 'bg-red-500', desc: 'Consultation dans la semaine', action: 'Biopsie recommandée, avis spécialisé' },
                        { niveau: 5, label: 'URGENCE', color: 'bg-red-700', desc: 'Biopsie urgente / consultation immédiate', action: 'Suspicion malignité - ne pas différer' },
                      ].map(u => (
                        <div key={u.niveau} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                          <div className={`w-12 h-12 ${u.color} rounded-full flex items-center justify-center text-white font-bold text-lg`}>
                            {u.niveau}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <h5 className="font-semibold text-slate-800">{u.label}</h5>
                            </div>
                            <p className="text-sm text-slate-600">{u.desc}</p>
                            <p className="text-xs text-slate-500 mt-1">→ {u.action}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Red Flags */}
                {referentielTab === 'redflags' && (
                  <div className="space-y-4">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                      <p className="text-sm text-red-700">
                        <strong>Red Flags</strong> = Signes d'alerte évoquant une pathologie maligne.
                        Leur présence nécessite une <strong>biopsie urgente</strong>.
                      </p>
                    </div>
                    <div className="space-y-2">
                      {[
                        { signe: 'Bords éversés', code: 'B22', susp: 'Carcinome', action: 'BIOPSIE URGENTE' },
                        { signe: 'Surface végétante', code: 'S20', susp: 'Carcinome', action: 'BIOPSIE URGENTE' },
                        { signe: 'Masse exophytique', code: 'S18', susp: 'Carcinome', action: 'BIOPSIE URGENTE' },
                        { signe: 'Surface mamelonnée', code: 'S06', susp: 'Carcinome', action: 'BIOPSIE URGENTE' },
                        { signe: 'Nodule / Tumeur', code: 'R11/R12', susp: 'Carcinome', action: 'BIOPSIE URGENTE' },
                        { signe: 'Pigmentation irrégulière', code: '-', susp: 'Mélanome', action: 'BIOPSIE URGENTE' },
                        { signe: 'Fond nécrotique', code: 'S16', susp: 'GUN, Carcinome', action: 'BIOPSIE URGENTE' },
                        { signe: 'Ulcération > 3 semaines', code: '-', susp: 'Carcinome', action: 'BIOPSIE URGENTE' },
                        { signe: 'Localisation à haut risque + masse', code: 'LANG_LAT, PLANCH', susp: 'Carcinome', action: 'BIOPSIE URGENTE' },
                      ].map((rf, i) => (
                        <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200">
                          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-slate-800">{rf.signe}</span>
                              {rf.code !== '-' && (
                                <span className="px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-mono">{rf.code}</span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500">Suspicion : {rf.susp}</p>
                          </div>
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-semibold">{rf.action}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Examens cliniques */}
                {referentielTab === 'examens' && (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600 mb-4">
                      3 examens cliniques complémentaires peuvent être suggérés pour affiner le diagnostic :
                    </p>
                    <div className="space-y-4">
                      <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
                        <h4 className="font-semibold text-sky-800 flex items-center gap-2 mb-2">
                          <span className="w-6 h-6 bg-sky-200 rounded-full flex items-center justify-center text-xs">1</span>
                          Palpation bimanuelle
                        </h4>
                        <p className="text-sm text-sky-700 mb-2">
                          <strong>Indication :</strong> Recherche d'induration
                        </p>
                        <p className="text-xs text-sky-600">
                          <strong>Pathologies concernées :</strong> Carcinome, Syphilis (chancre), Actinomycose, Fibrome
                        </p>
                        <div className="mt-2 p-2 bg-white rounded text-xs text-slate-600">
                          Une induration à la palpation est un signe d'alerte majeur pour le carcinome.
                        </div>
                      </div>

                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <h4 className="font-semibold text-purple-800 flex items-center gap-2 mb-2">
                          <span className="w-6 h-6 bg-purple-200 rounded-full flex items-center justify-center text-xs">2</span>
                          Vitropression
                        </h4>
                        <p className="text-sm text-purple-700 mb-2">
                          <strong>Indication :</strong> Confirmer la nature vasculaire
                        </p>
                        <p className="text-xs text-purple-600">
                          <strong>Pathologies concernées :</strong> Hémangiome
                        </p>
                        <div className="mt-2 p-2 bg-white rounded text-xs text-slate-600">
                          Une lésion vasculaire blanchit à la vitropression (pression avec lame de verre).
                        </div>
                      </div>

                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <h4 className="font-semibold text-amber-800 flex items-center gap-2 mb-2">
                          <span className="w-6 h-6 bg-amber-200 rounded-full flex items-center justify-center text-xs">3</span>
                          Test de grattage
                        </h4>
                        <p className="text-sm text-amber-700 mb-2">
                          <strong>Indication :</strong> Différencier candidose / leucoplasie
                        </p>
                        <p className="text-xs text-amber-600">
                          <strong>Pathologies concernées :</strong> Candidose, Leucoplasie
                        </p>
                        <div className="mt-2 p-2 bg-white rounded text-xs text-slate-600">
                          <strong>Détachable</strong> (laisse fond érythémateux) = Candidose<br/>
                          <strong>Non détachable</strong> = Leucoplasie (biopsie nécessaire)
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Codes sémiologiques */}
                {referentielTab === 'codes' && (
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600 mb-4">
                      L'analyse utilise des codes standardisés pour décrire les caractéristiques des lésions :
                    </p>

                    {/* Localisations */}
                    <details className="bg-slate-50 rounded-lg border border-slate-200">
                      <summary className="p-3 font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 rounded-lg">
                        Localisations (27 codes)
                      </summary>
                      <div className="p-3 pt-0 grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                        {[
                          ['LANG_DOS_ANT', 'Langue - Dos antérieur'],
                          ['LANG_DOS_MOY', 'Langue - Dos moyen'],
                          ['LANG_LAT_D/G', 'Langue - Bord latéral'],
                          ['LANG_VENTR', 'Langue - Face ventrale'],
                          ['PAL_DUR_MED', 'Palais dur - Médian'],
                          ['PAL_MOU', 'Palais mou'],
                          ['JUG_D/G', 'Joue (muqueuse jugale)'],
                          ['LEV_INF/SUP_MUQ', 'Lèvre - Muqueuse'],
                          ['LEV_INF/SUP_VERM', 'Lèvre - Vermillon'],
                          ['PLANCH_ANT', 'Plancher buccal'],
                          ['GENC_ATT', 'Gencive attachée'],
                          ['GENC_PAP', 'Gencive papillaire'],
                        ].map(([code, label]) => (
                          <div key={code} className="flex items-center gap-1">
                            <span className="font-mono text-indigo-600">{code}</span>
                            <span className="text-slate-500">= {label}</span>
                          </div>
                        ))}
                      </div>
                    </details>

                    {/* Couleurs */}
                    <details className="bg-slate-50 rounded-lg border border-slate-200">
                      <summary className="p-3 font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 rounded-lg">
                        Couleurs et nuances
                      </summary>
                      <div className="p-3 pt-0 space-y-2 text-xs">
                        <div className="font-medium text-slate-600 mb-1">Couleurs principales :</div>
                        <div className="flex flex-wrap gap-2">
                          {[
                            ['C01', 'Blanc', 'bg-white border'],
                            ['C02', 'Rouge', 'bg-red-500 text-white'],
                            ['C03', 'Rose', 'bg-pink-300'],
                            ['C05', 'Brun', 'bg-amber-700 text-white'],
                            ['C06', 'Noir', 'bg-black text-white'],
                            ['C07', 'Bleu', 'bg-blue-500 text-white'],
                            ['C08', 'Violet', 'bg-purple-500 text-white'],
                          ].map(([code, label, color]) => (
                            <span key={code} className={`px-2 py-1 rounded ${color}`}>
                              {code} = {label}
                            </span>
                          ))}
                        </div>
                        <div className="font-medium text-slate-600 mt-3 mb-1">Nuances importantes :</div>
                        <div className="flex flex-wrap gap-2">
                          <span className="px-2 py-1 bg-slate-200 rounded"><strong>C01b</strong> = Blanc crayeux (leucoplasie)</span>
                          <span className="px-2 py-1 bg-slate-200 rounded"><strong>C01c</strong> = Blanc laiteux (candidose)</span>
                        </div>
                      </div>
                    </details>

                    {/* Surface */}
                    <details className="bg-slate-50 rounded-lg border border-slate-200">
                      <summary className="p-3 font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 rounded-lg">
                        Aspects de surface
                      </summary>
                      <div className="p-3 pt-0 grid grid-cols-2 gap-2 text-xs">
                        {[
                          ['S01', 'Lisse'],
                          ['S11', 'Kératosique'],
                          ['S12', 'Atrophique'],
                          ['S13', 'Érosive'],
                          ['S14', 'Ulcérée'],
                          ['S15', 'Pseudomembraneuse'],
                          ['S16', 'Nécrotique ⚠️'],
                          ['S18', 'Exophytique ⚠️'],
                          ['S20', 'Végétante ⚠️'],
                          ['S06', 'Mamelonnée ⚠️'],
                        ].map(([code, label]) => (
                          <div key={code} className={`flex items-center gap-1 ${label.includes('⚠️') ? 'text-red-600 font-medium' : ''}`}>
                            <span className="font-mono">{code}</span>
                            <span>= {label}</span>
                          </div>
                        ))}
                      </div>
                    </details>

                    {/* Relief */}
                    <details className="bg-slate-50 rounded-lg border border-slate-200">
                      <summary className="p-3 font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 rounded-lg">
                        Types de relief
                      </summary>
                      <div className="p-3 pt-0 grid grid-cols-2 gap-2 text-xs">
                        {[
                          ['R01', 'Macule (plane)'],
                          ['R02', 'Plaque'],
                          ['R11', 'Nodule ⚠️'],
                          ['R12', 'Tumeur ⚠️'],
                          ['R13', 'Vésicule'],
                          ['R17', 'Masse ⚠️'],
                          ['R20', 'Érosion'],
                          ['R22', 'Ulcération'],
                          ['R25', 'Cratériforme'],
                        ].map(([code, label]) => (
                          <div key={code} className={`flex items-center gap-1 ${label.includes('⚠️') ? 'text-red-600 font-medium' : ''}`}>
                            <span className="font-mono">{code}</span>
                            <span>= {label}</span>
                          </div>
                        ))}
                      </div>
                    </details>

                    {/* Bords */}
                    <details className="bg-slate-50 rounded-lg border border-slate-200">
                      <summary className="p-3 font-semibold text-slate-700 cursor-pointer hover:bg-slate-100 rounded-lg">
                        Caractéristiques des bords
                      </summary>
                      <div className="p-3 pt-0 space-y-3 text-xs">
                        <div>
                          <div className="font-medium text-slate-600 mb-1">Netteté :</div>
                          <div className="flex flex-wrap gap-2">
                            <span className="px-2 py-1 bg-slate-200 rounded">B01 = Nets</span>
                            <span className="px-2 py-1 bg-slate-200 rounded">B02 = Moyens</span>
                            <span className="px-2 py-1 bg-slate-200 rounded">B03 = Flous</span>
                          </div>
                        </div>
                        <div>
                          <div className="font-medium text-slate-600 mb-1">Relief des bords :</div>
                          <div className="flex flex-wrap gap-2">
                            <span className="px-2 py-1 bg-slate-200 rounded">B20 = Plats</span>
                            <span className="px-2 py-1 bg-slate-200 rounded">B21 = Surélevés</span>
                            <span className="px-2 py-1 bg-red-200 text-red-800 rounded font-medium">B22 = Éversés ⚠️</span>
                          </div>
                        </div>
                      </div>
                    </details>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
                <p className="text-xs text-slate-500 text-center">
                  Référentiel clinique V4.0 • 35 pathologies • Dernière mise à jour : Janvier 2025
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
